import { AxiosResponse } from "axios";
import { toast } from 'react-toastify';
import { store } from '@store';
import { AuthErrorCode } from '../../common/errors/auth-error-code.enum';

export const DEFAULT_HTTP_RESPONSE: HttpResponse = {
  status: null,
  errMessage: '',
};

export enum RequestStatus {
  SUCCESS = 'success',
  FAILED = 'failed',
  LOADING = 'loading',
}

export interface RequestState<T> {
  data: T | null;
  status: RequestStatus;
  error: unknown;
}

export function handleError(err: unknown) {
  if (err instanceof Error) {
    return err.message;
  }
  return err;
}

export function handleMutationResponse(mutationResponse: AxiosResponse, mutationName: string) {
  const responseData = mutationResponse?.data;

  if (responseData?.errors?.length > 0) {
    throw new Error(responseData?.errors[0]?.message);
  }

  return responseData?.data?.[mutationName];
}
/** Error codes meaning the session is no longer valid, whatever the message says. */
const SESSION_ERROR_CODES: string[] = ['UNAUTHENTICATED', AuthErrorCode.MissingOrInvalidDeviceId];

/** A GraphQL error, keeping the `extensions.code` the API sends with it. */
export class GraphQLQueryError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

/** Data of one root field of a query response; throws on any GraphQL error in the response. */
export function handleQueryResponse<T>(queryResponse: AxiosResponse, fieldName: string): T {
  const error = queryResponse?.data?.errors?.[0];

  if (error) {
    throw new GraphQLQueryError(error.message, error.extensions?.code);
  }

  return queryResponse?.data?.data?.[fieldName];
}

export function isSessionError(err: unknown): boolean {
  if (err instanceof GraphQLQueryError) {
    return (!!err.code && SESSION_ERROR_CODES.includes(err.code)) || err.message === 'Unauthorized';
  }
  return (err as { response?: { status?: number } })?.response?.status === 401;
}

/**
 * Error handling for page-data queries: an invalid session signs the user out (back to login),
 * anything else is shown as a toast.
 */
export function handleQueryError(err: unknown, response?: HttpResponse) {
  if (response) {
    response.status = 'badRequest';
    response.errMessage = 'Bad Request';
  }

  if (isSessionError(err)) {
    store.userStore.clearSession();
    return;
  }

  toast(`Something went wrong: ${handleError(err)}`, { type: 'error' });
}
